import type { RefObject } from 'react';

import type {
	SelectionExtensionPlugin,
	SelectionExtensionPluginOptions,
	ExtensionConfiguration,
} from '@atlaskit/editor-plugin-selection-extension';
import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';

import type { PageMode } from '@confluence/issue-create-side-panel/entry-points/IssueCreateSidePanelContextProvider.ts';
import { useEditorToolbarExtensions } from '@confluence/highlight-actions/entry-points/useEditorToolbarExtensions.ts';
import { useRemixExtension } from './useRemixExtension.tsx';
import { useJiraWorkCreateSelectionExtension } from '@confluence/jira-work-create-extension/entry-points/useJiraWorkCreateSelectionExtension.ts';
import { useJiraWorkCreateSelectionExtensionV2 } from '@confluence/jira-work-create-extension/entry-points/useJiraWorkCreateSelectionExtensionV2.ts';
import type { RightSidebarContextType } from '@confluence/page-layout-context';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useTrelloAccess } from '@confluence/highlight-actions/entry-points/useTrelloAccess.ts';

import { useTrelloInboxSelectionExtension } from './useTrelloInboxSelectionExtension.tsx';

export const useSelectionExtensionPluginOptions = ({
	isSidePanelOpenRef,
	rightSidebarContextRef,
	pageModeRef,
	hasEditPermissionRef,
	contentId,
	editorAPIRef,
	flags,
	snippetExtensionList = [],
}: {
	isSidePanelOpenRef: React.MutableRefObject<boolean>;
	rightSidebarContextRef: React.MutableRefObject<RightSidebarContextType>;
	pageModeRef: React.MutableRefObject<PageMode>;
	hasEditPermissionRef: React.MutableRefObject<boolean>;
	contentId: string | undefined;
	editorAPIRef: RefObject<PublicPluginAPI<[SelectionExtensionPlugin]> | null | undefined>;
	flags?: FlagsStateContainer;
	snippetExtensionList?: ExtensionConfiguration[];
}): SelectionExtensionPluginOptions => {
	const useRemixExtensionWithRef = () => useRemixExtension(editorAPIRef);
	const useConditionalRemixExtension = conditionalHooksFactory(
		() => expValEquals('cc-mui-experiment', 'isEnabled', true),
		useRemixExtensionWithRef,
		() => ({ extensionList: [] }),
	);

	const { extensionList: remixExtensionList } = useConditionalRemixExtension();

	const jiraWorkCreateSelectionExtension = useJiraWorkCreateSelectionExtension({
		isSidePanelOpenRef,
		rightSidebarContextRef,
		pageModeRef,
		hasEditPermissionRef,
	});

	const jiraWorkCreateExtensionV2 = useJiraWorkCreateSelectionExtensionV2({
		rightSidebarContextRef,
		pageModeRef,
		hasEditPermissionRef,
		editorAPIRef,
	});

	// Connect and Forge 2P extensions
	const useExternalExtensionsWithContentId = () => useEditorToolbarExtensions(contentId);
	const useConditionalExternalExtensions = conditionalHooksFactory(
		() => expVal('confluence_enable_2p_toolbar_apps', 'isEnabled', false),
		useExternalExtensionsWithContentId,
		() => [],
	);

	const externalExtensions = useConditionalExternalExtensions();
	const { hasTrelloAccess, loading: trelloAccessLoading } = useTrelloAccess();
	const trelloExtension: ExtensionConfiguration = useTrelloInboxSelectionExtension(
		contentId,
		editorAPIRef,
		flags,
	);
	const jiraWorkCreateExtensionList = expVal(
		'confluence-bulk-work-create-editor',
		'isEnabled',
		false,
	)
		? jiraWorkCreateExtensionV2
		: [];

	const shouldShowTrelloExtension =
		flags && !trelloAccessLoading && hasTrelloAccess && fg('quick_capture_trello_inbox');

	const extensionList = [
		...remixExtensionList,
		...snippetExtensionList,
		...externalExtensions,
		...jiraWorkCreateExtensionList,
		...(shouldShowTrelloExtension ? [trelloExtension] : []),
	];

	return {
		extensions: expVal('confluence-bulk-work-create-editor', 'isEnabled', false)
			? {}
			: { firstParty: [jiraWorkCreateSelectionExtension] },
		extensionList,
	};
};
